import { components } from "@/api-schema";
import { cn } from "@/common/helpers/utils";
import { DeviceType } from "@/common/hooks/use-device";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import i18next from "i18next";
import { commonColumns } from "../_constants/common-columns";

export type ApplicationAppRoleTableType = components["schemas"]["Application"];

type AppRole = components["schemas"]["AppRole"];
type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];

export const applicationAppRoleTableDefaultColumns = {
  columnName: {
    ...commonColumns.columnName<ApplicationAppRoleTableType>(),
    size: 1000,
    header: () => i18next.t("ram:Application"),
  } as ImplementedAccessorColumnDef<ApplicationAppRoleTableType>,
  columnSelected: {
    id: "selected",
    size: 150,
    enableSorting: false,
    header: () => i18next.t("common:Selected"),
    align: "right",
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => ({ appRoles: row.appRoles, applicationUuid: row.uuid }),
    cell: ({ getValue, table }) => {
      const { appRoles, applicationUuid } = getValue() as {
        appRoles: AppRole[] | undefined;
        applicationUuid: string;
      };

      if (!appRoles || appRoles.length === 0) {
        return (
          <div className="text-right">
            <EmptyValue />
          </div>
        );
      }

      const regularAppRoles = appRoles?.filter((appRole) => !appRole.attribute) || [];
      const attributesByUuid = table.options.meta?.attributesByUuid as Map<string, AppRoleAttribute> | undefined;
      const attributeCount = countAttributesForApplication(attributesByUuid, applicationUuid);

      const totalCount = regularAppRoles.length + attributeCount;
      const selectedUuids = (table.options.meta?.selected as string[]) || [];
      const selectedRegularCount = countSelectedRoles(selectedUuids, regularAppRoles);
      const selectedCount = selectedRegularCount + attributeCount;
      const colorClass = getSelectionColorClass(selectedCount, totalCount);

      return (
        <div className={cn("flex shrink flex-row justify-end gap-2 rounded-md", colorClass)}>
          <div className="min-w-6 text-center">{selectedCount}</div>
          <span>/</span>
          <div className="min-w-6 text-center">{totalCount}</div>
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<ApplicationAppRoleTableType>,
  columnAppRole: {
    id: "appRoles",
    size: 800,
    enableSorting: false,
    header: () => i18next.t("ram:AppRoles"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => ({ appRoles: row.appRoles, applicationUuid: row.uuid }),
    cell: ({ getValue, table }) => {
      const { appRoles, applicationUuid } = getValue() as {
        appRoles: AppRole[] | undefined;
        applicationUuid: string;
      };

      if (!appRoles || appRoles.length === 0) {
        return (
          <div className="text-left">
            <EmptyValue />
          </div>
        );
      }

      const regularAppRoles = appRoles?.filter((appRole) => !appRole.attribute) || [];
      const attributesByUuid = table.options.meta?.attributesByUuid as Map<string, AppRoleAttribute> | undefined;
      const attributeCount = countAttributesForApplication(attributesByUuid, applicationUuid);
      const totalItems = regularAppRoles.length + attributeCount;
      const displayLimit = 2;
      const visibleRoles = regularAppRoles.slice(0, displayLimit);
      const remainingCount = totalItems - visibleRoles.length;
      const showAttributeBadge = attributeCount > 0 && visibleRoles.length < displayLimit;
      const showRemainingBadge = remainingCount > 0 && visibleRoles.length === displayLimit;

      return (
        <div className="flex flex-wrap gap-1">
          {visibleRoles.map((appRole) => (
            <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Secondary} key={appRole.uuid}>
              {appRole.name}
            </Badge>
          ))}
          {showAttributeBadge && (
            <Badge variant={BadgeVariant.Filled} colorVariant={BadgeColorVariant.Secondary}>
              +{attributeCount} {i18next.t("common:Attributes")}
            </Badge>
          )}
          {showRemainingBadge && (
            <Badge variant={BadgeVariant.Filled} colorVariant={BadgeColorVariant.Secondary}>
              +{remainingCount} {i18next.t("common:More")}
            </Badge>
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<ApplicationAppRoleTableType>,
  columnUuid: commonColumns.columnUuid<ApplicationAppRoleTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<ApplicationAppRoleTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<ApplicationAppRoleTableType>(),
};

function countSelectedRoles(selectedUuids: string[], appRoles: AppRole[]): number {
  return selectedUuids.filter((uuid) => appRoles.some((role) => role.uuid === uuid)).length;
}

function countAttributesForApplication(
  attributesByUuid: Map<string, AppRoleAttribute> | undefined,
  applicationUuid: string,
): number {
  if (!attributesByUuid) {
    return 0;
  }

  return Array.from(attributesByUuid.values()).filter(
    (attr) => attr.application && typeof attr.application === "object" && attr.application.uuid === applicationUuid,
  ).length;
}

function getSelectionColorClass(selectedCount: number, totalCount: number): string {
  if (selectedCount === 0) return "text-error";
  if (selectedCount < totalCount) return "text-warning";
  return "text-success";
}
